import { db, SyncQueueItem } from '../db/indexedDB';
import { getSites } from './siteService';
import { getAssets, getAsset } from './assetService';
import { getOrganization } from './authService';
import { getFloc } from './flocService';
import { getTemplate } from './templateService';
import { getMeasurementDefinitions } from './measurementDefinitionService';
import { getReservedInspections } from './inspectionService';

/**
 * Sync service for managing offline data synchronization
 */
export class SyncService {
  private isOnline: boolean = navigator.onLine;
  private syncInProgress: boolean = false;
  private orgId: string | null = null;

  constructor() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      console.log('Network: Online');
      this.isOnline = true;
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      console.log('Network: Offline');
      this.isOnline = false;
    });
  }

  /**
   * Initialize sync service with organization ID
   */
  setOrgId(orgId: string): void {
    this.orgId = orgId;
  }

  /**
   * Check if currently online
   */
  getOnlineStatus(): boolean {
    return this.isOnline;
  }

  /**
   * Sync data for reserved jobs from Firestore to IndexedDB
   * This performs targeted sync - only downloading data needed for reserved inspections
   */
  async syncAllData(orgId: string, userId?: string): Promise<void> {
    if (!this.isOnline) {
      console.log('Cannot sync: offline');
      return;
    }

    console.log('Starting targeted data sync...');

    try {
      // Sync organization
      const org = await getOrganization(orgId);
      if (org) {
        await db.organization.put(org);
      }

      // If userId is provided, perform targeted sync for reserved jobs
      if (userId) {
        console.log('Performing targeted sync for reserved jobs...');

        // Get reserved inspections for this user
        const reservedInspections = await getReservedInspections(orgId, userId);
        console.log(`Found ${reservedInspections.length} reserved inspections`);

        // Extract unique IDs from reserved inspections
        const flocIds = new Set<string>();
        const assetIds = new Set<string>();
        const templateIds = new Set<string>();

        reservedInspections.forEach(inspection => {
          if (inspection.flocId) flocIds.add(inspection.flocId);
          if (inspection.assetId) assetIds.add(inspection.assetId);
          if (inspection.templateId) templateIds.add(inspection.templateId);
        });

        console.log(`Unique FLOCs: ${flocIds.size}, Assets: ${assetIds.size}, Templates: ${templateIds.size}`);

        // Sync FLOCs
        const flocs = [];
        for (const flocId of flocIds) {
          try {
            const floc = await getFloc(orgId, flocId);
            if (floc) flocs.push(floc);
          } catch (error) {
            console.warn(`Failed to fetch FLOC ${flocId}:`, error);
          }
        }
        if (flocs.length > 0) {
          await db.flocs.bulkPut(flocs);
          console.log(`Synced ${flocs.length} FLOCs`);
        }

        // Sync Assets
        const assets = [];
        for (const assetId of assetIds) {
          try {
            const asset = await getAsset(orgId, assetId);
            if (asset) assets.push(asset);
          } catch (error) {
            console.warn(`Failed to fetch Asset ${assetId}:`, error);
          }
        }
        if (assets.length > 0) {
          await db.assets.bulkPut(assets);
          console.log(`Synced ${assets.length} assets`);
        }

        // Sync Templates
        const templates = [];
        for (const templateId of templateIds) {
          try {
            const template = await getTemplate(orgId, templateId);
            if (template) templates.push(template);
          } catch (error) {
            console.warn(`Failed to fetch Template ${templateId}:`, error);
          }
        }
        if (templates.length > 0) {
          await db.templates.bulkPut(templates);
          console.log(`Synced ${templates.length} templates`);
        }

        // Sync measurement definitions (all of them, as they're needed for templates)
        const measurementDefinitions = await getMeasurementDefinitions(orgId);
        await db.measurementDefinitions.bulkPut(measurementDefinitions);
        console.log(`Synced ${measurementDefinitions.length} measurement definitions`);

        // Sync the reserved inspections themselves
        await db.inspections.bulkPut(reservedInspections as any);
        console.log(`Synced ${reservedInspections.length} reserved inspections`);

      } else {
        // Fallback to full sync if no userId provided
        console.log('No userId provided, performing basic sync...');

        // Sync sites
        const sites = await getSites(orgId);
        await db.sites.bulkPut(sites);
        console.log(`Synced ${sites.length} sites`);

        // Sync assets
        const assets = await getAssets(orgId);
        await db.assets.bulkPut(assets);
        console.log(`Synced ${assets.length} assets`);
      }

      console.log('Targeted data sync completed');
    } catch (error) {
      console.error('Failed to sync data:', error);
      throw error;
    }
  }

  /**
   * Add an operation to the sync queue
   */
  async queueOperation(
    type: 'create' | 'update' | 'delete',
    collection: 'sites' | 'assets' | 'inspections' | 'templates',
    docId: string,
    data?: any
  ): Promise<void> {
    const queueItem: SyncQueueItem = {
      type,
      collection,
      docId,
      data,
      timestamp: Date.now(),
      attempts: 0,
    };

    await db.syncQueue.add(queueItem);
    console.log('Queued operation:', queueItem);

    // Try to process immediately if online
    if (this.isOnline) {
      this.processSyncQueue();
    }
  }

  /**
   * Process pending operations in the sync queue
   */
  async processSyncQueue(): Promise<void> {
    if (!this.isOnline || this.syncInProgress || !this.orgId) {
      return;
    }

    this.syncInProgress = true;
    console.log('Processing sync queue...');

    try {
      const queueItems = await db.syncQueue.orderBy('timestamp').toArray();

      for (const item of queueItems) {
        try {
          await this.processQueueItem(item);
          // Remove from queue after successful processing
          await db.syncQueue.delete(item.id!);
          console.log('Processed queue item:', item);
        } catch (error: any) {
          console.error('Failed to process queue item:', item, error);

          // Update attempts and error
          await db.syncQueue.update(item.id!, {
            attempts: item.attempts + 1,
            lastError: error.message,
          });

          // Remove if too many attempts
          if (item.attempts >= 3) {
            console.error('Max attempts reached, removing from queue:', item);
            await db.syncQueue.delete(item.id!);
          }
        }
      }

      console.log('Sync queue processed');
    } catch (error) {
      console.error('Failed to process sync queue:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Process a single queue item
   */
  private async processQueueItem(item: SyncQueueItem): Promise<void> {
    if (!this.orgId) {
      throw new Error('Organization ID not set');
    }

    // Import services dynamically to avoid circular dependencies
    const { createSite, updateSite, deleteSite } = await import('./siteService');
    const { createAsset, updateAsset, deleteAsset } = await import('./assetService');

    switch (item.collection) {
      case 'sites':
        if (item.type === 'create') {
          await createSite(this.orgId, item.data);
        } else if (item.type === 'update') {
          await updateSite(this.orgId, item.docId, item.data);
        } else if (item.type === 'delete') {
          await deleteSite(this.orgId, item.docId);
        }
        break;

      case 'assets':
        if (item.type === 'create') {
          // Use 'sync' as userId for offline sync operations
          await createAsset(this.orgId, item.data, 'sync');
        } else if (item.type === 'update') {
          await updateAsset(this.orgId, item.docId, item.data, 'sync');
        } else if (item.type === 'delete') {
          await deleteAsset(this.orgId, item.docId);
        }
        break;

      // Add more collections as needed
      default:
        console.warn('Unknown collection:', item.collection);
    }
  }

  /**
   * Get sync queue status
   */
  async getSyncQueueStatus(): Promise<{
    pending: number;
    items: SyncQueueItem[];
  }> {
    const items = await db.syncQueue.toArray();
    return {
      pending: items.length,
      items,
    };
  }

  /**
   * Clear sync queue
   */
  async clearSyncQueue(): Promise<void> {
    await db.syncQueue.clear();
  }

  /**
   * Get storage info
   */
  async getStorageInfo(): Promise<{
    usage: number;
    quota: number;
    usagePercent: number;
    usageMB: number;
    quotaMB: number;
  }> {
    const { usage, quota } = await db.getStorageEstimate();
    return {
      usage,
      quota,
      usagePercent: quota > 0 ? (usage / quota) * 100 : 0,
      usageMB: usage / (1024 * 1024),
      quotaMB: quota / (1024 * 1024),
    };
  }
}

// Create singleton instance
export const syncService = new SyncService();
